import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavigationMenu } from './NavigationMenu';

interface LoadingAnimationProps {
  withNavigation?: boolean;
  size?: 'small' | 'large';
  text?: string;
  className?: string;
}

const loadingTexts = [
  "Подготовка нейросети...",
  "Загрузка данных...",
  "Анализ информации...",
  "Генерация ответа...",
  "Почти готово...",
];

// Генерируем точки в круговой области
const generateNeurons = () => {
  const neurons = [];
  
  // Добавляем центральный нейрон
  neurons.push({
    x: 50,
    y: 50,
    size: 3,
    pulseSpeed: 0.8,
    isCenter: true
  });
  
  // Определяем круговые орбиты с разными радиусами
  const orbits = [
    { radius: 15, count: 3 },  // ближняя орбита
    { radius: 30, count: 6 }   // дальняя орбита - делаем 6 нейронов вместо 5
  ];
  
  // Добавляем нейроны по орбитам
  orbits.forEach((orbit, orbitIndex) => {
    for (let i = 0; i < orbit.count; i++) {
      // Распределяем точки равномерно по окружности, но со смещением для разных орбит
      const angle = (i / orbit.count) * Math.PI * 2 + (orbitIndex * 0.4);
      // Случайное отклонение от идеальной орбиты
      const randomRadius = orbit.radius + (Math.random() * 6 - 3);
      
      neurons.push({
        x: 50 + randomRadius * Math.cos(angle),
        y: 50 + randomRadius * Math.sin(angle),
        size: Math.random() * 1.5 + 1.5,
        pulseSpeed: Math.random() * 1 + 0.5,
        orbitIndex: orbitIndex,
        angle: angle
      });
    }
  });
  
  return neurons;
};

// Функция для проверки пересечения двух отрезков
const doLinesIntersect = (line1: any, line2: any) => {
  // Получаем координаты линий
  const x1 = line1.x1, y1 = line1.y1;
  const x2 = line1.x2, y2 = line1.y2;
  const x3 = line2.x1, y3 = line2.y1;
  const x4 = line2.x2, y4 = line2.y2;
  
  // Находим определители
  const den = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  
  // Если определитель равен 0, линии параллельны
  if (den === 0) {
    return false;
  }
  
  // Находим параметры t и u
  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / den;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / den;
  
  // Проверяем, находится ли пересечение в пределах обоих отрезков
  if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
    // Игнорируем соединения с общей точкой
    if ((x1 === x3 && y1 === y3) || (x1 === x4 && y1 === y4) || 
        (x2 === x3 && y2 === y3) || (x2 === x4 && y2 === y4)) {
      return false;
    }
    return true;
  }
  
  return false;
};

// Определяем правила соединения нейронов
const shouldConnect = (neuron1: any, neuron2: any, orbits?: any[]) => {
  // Соединяем центральный нейрон с каждым нейроном внутреннего круга
  if (neuron1.isCenter || neuron2.isCenter) {
    const nonCenterNeuron = neuron1.isCenter ? neuron2 : neuron1;
    // Соединяем центр со всеми нейронами внутреннего круга
    return nonCenterNeuron.orbitIndex === 0;
  }
  
  // Считаем расстояние между нейронами
  const dx = neuron1.x - neuron2.x;
  const dy = neuron1.y - neuron2.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Соединяем нейроны на соседних орбитах с учетом расстояния
  if (Math.abs(neuron1.orbitIndex - neuron2.orbitIndex) === 1) {
    // Проверяем угол между нейронами, чтобы минимизировать пересечения
    const angleDiff = Math.abs(neuron1.angle - neuron2.angle);
    const normalizedAngleDiff = Math.min(angleDiff, Math.PI * 2 - angleDiff);
    
    // Расширяем диапазон угла для увеличения количества соединений,
    // но все же ограничиваем его, чтобы избежать пересечений
    return normalizedAngleDiff < 0.8 && distance < 40;
  }
  
  // Соединяем нейроны на одной орбите
  if (neuron1.orbitIndex === neuron2.orbitIndex && orbits) {
    const orbitCount = orbits[neuron1.orbitIndex]?.count || 5;
    const angleDiff = Math.abs(neuron1.angle - neuron2.angle);
    const normalizedAngleDiff = Math.min(angleDiff, Math.PI * 2 - angleDiff);
    
    const angleThreshold = Math.PI * 2 / orbitCount;
    
    // Соединяем с ближайшими соседями
    if (normalizedAngleDiff < angleThreshold * 1.1) {
      // Предотвращаем замыкание кольца
      const firstAngleOnOrbit = (0 / orbitCount) * Math.PI * 2 + (neuron1.orbitIndex * 0.4);
      const lastAngleOnOrbit = ((orbitCount - 1) / orbitCount) * Math.PI * 2 + (neuron1.orbitIndex * 0.4);
      
      const isFirstLastConnection = 
        (Math.abs(neuron1.angle - firstAngleOnOrbit) < 0.1 && Math.abs(neuron2.angle - lastAngleOnOrbit) < 0.1) || 
        (Math.abs(neuron2.angle - firstAngleOnOrbit) < 0.1 && Math.abs(neuron1.angle - lastAngleOnOrbit) < 0.1);
      
      return !isFirstLastConnection;
    }
    
    // Добавляем некоторые диагональные соединения (через один нейрон), чтобы было больше связей
    // но только для внутренней орбиты, чтобы избежать пересечений
    if (neuron1.orbitIndex === 0 && normalizedAngleDiff < angleThreshold * 2.2) {
      return Math.random() < 0.5; // 50% вероятность соединения
    }
    
    return false;
  }
  
  return false;
};

export function LoadingAnimation({ 
  withNavigation = false, 
  size = 'large',
  text,
  className = ''
}: LoadingAnimationProps) {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  
  // Генерируем нейронную сеть
  const [neurons, setNeurons] = useState(() => generateNeurons());
  const orbitsRef = useRef([
    { radius: 15, count: 3 },  // ближняя орбита
    { radius: 30, count: 6 }   // дальняя орбита
  ]);
  
  // Создаем соединения между нейронами
  const [connections] = useState(() => {
    const lines = [];
    const orbits = orbitsRef.current;
    
    // Создаем потенциальные соединения
    const potentialConnections = [];
    for (let i = 0; i < neurons.length; i++) {
      for (let j = i + 1; j < neurons.length; j++) {
        if (shouldConnect(neurons[i], neurons[j], orbits)) {
          potentialConnections.push({
            x1: neurons[i].x,
            y1: neurons[i].y,
            x2: neurons[j].x,
            y2: neurons[j].y,
            neuron1Index: i,
            neuron2Index: j,
            distance: Math.sqrt(
              Math.pow(neurons[i].x - neurons[j].x, 2) + 
              Math.pow(neurons[i].y - neurons[j].y, 2)
            ),
            duration: Math.random() * 2 + 2,
            delay: Math.random() * 1,
            width: Math.random() * 0.5 + 0.3
          });
        }
      }
    }
    
    // Сортируем соединения от короткого к длинному - приоритет коротким соединениям
    potentialConnections.sort((a, b) => a.distance - b.distance);
    
    // Отфильтровываем пересекающиеся соединения
    for (let i = 0; i < potentialConnections.length; i++) {
      const connection = potentialConnections[i];
      let intersects = false;
      
      // Проверяем на пересечение с уже добавленными соединениями
      for (let j = 0; j < lines.length; j++) {
        if (doLinesIntersect(connection, lines[j])) {
          intersects = true;
          break;
        }
      }
      
      // Если нет пересечений, добавляем соединение
      if (!intersects) {
        lines.push(connection);
      }
    }
    
    return lines;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % loadingTexts.length);
    }, 2500); // Меняем текст каждые 2.5 секунды
    return () => clearInterval(interval);
  }, []);

  const containerSize = size === 'small' ? 'w-16 h-16' : 'w-32 h-32';
  const textClasses = size === 'small' ? 'mt-4 text-sm' : 'mt-8 text-lg';

  const content = (
    <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
      <div className={`relative ${containerSize}`}>
        {/* Нейронная сеть */}
        <motion.svg 
          viewBox="0 0 100 100" 
          className="absolute inset-0 w-full h-full"
          initial={{ rotate: 0 }}
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 70, ease: "linear", repeat: Infinity }}
        >
          {/* Соединения между нейронами (прямые линии) */}
          {connections.map((connection, i) => (
            <motion.line
              key={`line-${i}`}
              x1={connection.x1}
              y1={connection.y1}
              x2={connection.x2}
              y2={connection.y2}
              stroke="rgba(96, 165, 250, 0.7)"
              strokeWidth={connection.width}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ 
                pathLength: [0, 1, 0],
                opacity: [0, 0.8, 0]
              }}
              transition={{ 
                duration: connection.duration, 
                delay: connection.delay, 
                repeat: Infinity, 
                repeatType: "loop", 
                ease: "easeInOut",
                repeatDelay: Math.random() * 1
              }}
            />
          ))}

          {/* Нейроны (точки) */}
          {neurons.map((neuron, i) => (
            <motion.circle
              key={`neuron-${i}`}
              cx={neuron.x}
              cy={neuron.y}
              r={neuron.size}
              fill="#3b82f6"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: [0, 1, 0.7, 1, 0],
                opacity: [0, 1, 0.7, 1, 0]
              }}
              transition={{ 
                duration: neuron.pulseSpeed * 4, 
                delay: i * 0.1, 
                repeat: Infinity, 
                repeatType: "loop",
                ease: "easeInOut",
                times: [0, 0.2, 0.5, 0.8, 1],
                repeatDelay: Math.random() * 0.5
              }}
            />
          ))}
        </motion.svg>
      </div>
      
      {/* Отображаем текст только если он явно передан в props */}
      {text && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5 }}
          className={`${textClasses} text-gray-600 dark:text-gray-300 font-medium text-center`}
        >
          {text}
        </motion.p>
      )}
    </div>
  );

  if (withNavigation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <NavigationMenu />
        <div className="flex items-center justify-center h-[calc(100vh-96px)]">
          {content}
        </div>
      </div>
    );
  }

  return content;
}